/**
 * HighlightManager
 * * Manages hex and unit highlighting in battle.
 * Реактивний підхід — підписується на events і сам відновлює hover стан.
 * InputController більше не потрібен для відновлення після анімацій.
 */

import type { EntityRegistry } from "@/core/battle/EntityRegistry";
import { HEX_HIGHLIGHT_COLOR } from "@/core/constants/battle.constants";
import { FactionType } from "@/core/types/battle.types";

import type { BattleHex } from "@/view/renderers/BattleHex";
import { coordsToId, pixelToAxial } from '@/core/utils/hex.utils';
import { useBattleStore } from "@/store/useBattleStore";
import { ControlStateMachine } from "@/core/battle/ControlStateMachine";
import { isUnitFinished, isUnitInActivePool } from "@/store/battleSelectors";
import { BattleBus } from "@/events/Events";
import { PointerState } from '@/core/utils/pointerState';

 export const HEX_STYLES = {
    // Зона руху: ледь помітне затемнення з оливковим відтінком
    REACHABLE: {
        tint: 0x2d3436,         // Темний графіт/оливковий
        fillAlpha: 0.15,
        borderAlpha: 0,
        borderThickness: 0,
        scale: 1,
        blendMode: 'multiply'
    },
    REACHABLE_HOVER: {
        tint: 0x2d3436,
        fillAlpha: 0.35,        // Просто стає щільнішим
        borderAlpha: 0,
        borderThickness: 0,
        scale: 1,
        blendMode: 'normal'
    },
    ACTIVE_UNIT: {
        tint: 0xe8dfd0,        // Базовий бронзовий (Bronze metal)
        // tint: 0xd9cbb3,        // Базовий бронзовий (Bronze metal)
        fillAlpha: 0.5,        // Дуже тонка заливка під юнітом
        borderAlpha: 0,
        borderThickness: 0,    // Чітка рамка
        scale: 1,
        // Світліший колір для світіння, щоб він "горів"
        glow: { distance: 10, strength: 3, color: 0xffd700 }, // Gold колір для glow
        // blendMode 'add' зробить метал більш блискучим
        blendMode: 'normal',
        isPulse: 1,
    },
    INSPECTED_UNIT: {
        tint: 0xffe555,        // Базовий бронзовий (Bronze metal)
        // tint: 0xd9cbb3,        // Базовий бронзовий (Bronze metal)
        fillAlpha: 0.25,        // Дуже тонка заливка під юнітом
        borderAlpha: 0,
        borderThickness: 0,    // Чітка рамка
        scale: 1,
    },
    // Зона контролю: колір іржі або старої крові
    ZOC: {
        tint: 0x4a0000, 
        fillAlpha: 0.35,
        borderAlpha: 0,
        borderThickness: 0,
        scale: 1,
    },
    ZOC_HOVER: {
        tint: 0x4a0000,
        fillAlpha: 0.4,
        borderAlpha: 0,
        borderThickness: 0,
        scale: 1,
    },
    // Атака: не "лазерний" червоний, а приглушений цегляний
    ATTACK_BASE: {
        tint: 0x8b0000,
        fillAlpha: 0.35,
        borderAlpha: 0.25,
        borderThickness: 12,
        scale: 1,
    },
    ATTACK_HOVER: {
        tint: 0x8b0000,
        fillAlpha: 0.5,
        borderAlpha: 0,
        borderThickness: 12,
        scale: 1,
    },
    // Маркер прицілу (маленький гекс в центрі)
    ATTACK_EMPTY: {
        tint: 0xffffff,
        fillAlpha: 0.2,
        borderAlpha: 0,
        borderThickness: 0,
        scale: 0.4,
    },
    // Звичайний ховер по порожньому гексу
    HOVER_DEFAULT: {
        tint: 0xffffff,
        fillAlpha: 0.1,
        borderAlpha: 0,
        borderThickness: 0,
        scale: 1,
    }
};


export class HighlightManager {
    private currentHoverState: {
        hexId: string | null;
        unitId: string | null;
    } = { hexId: null, unitId: null };


    constructor(
        private registry: EntityRegistry,
        private stateMachine: ControlStateMachine
    ) {
        // BattleBus.on('MOUSE_MOVE', ({ x, y }: { x: number; y: number }) => {
        //     this.lastMousePosition = { x, y };
        // });
        
        BattleBus.on('HEX_HOVER', ({ id }) => {
            if (!this.stateMachine.allowsHover()) return;
            const unitId = this.registry.getHex(id)?.data.entityId ?? null;
            // Використовуємо єдиний метод замість ручного оновлення
            this.updateHoverState(id, unitId); 
        });

        BattleBus.on('HEX_OUT', () => {
            if (!this.stateMachine.allowsHover()) return;
            this.clearHoverState();
        });
    }

    // ═══════════════════════════════════════════════════════════════════════
    // CORE: Single source of truth для рендерингу підсвітки
    // ═══════════════════════════════════════════════════════════════════════

    private refreshAllHighlights(): void {
        const state = useBattleStore.getState(); // ← Отримуємо стан один раз перед циклом
        
        if (!this.currentHoverState.hexId && PointerState) {
            const hex = this.findHexAtPosition(PointerState.x, PointerState.y);

            if (hex) {
                const unitId = state.battleMap.get(hex.id)?.entityId ?? null;
                this.currentHoverState = { hexId: hex.id, unitId };
            }
        }

        this.registry.getAllHexes().forEach((_hex, id) => {
            this.renderHexHighlight(id, state); // ← Передаємо стан
        });

        // if (this.currentHoverState.unitId) {
        //     this.highlightUnit(this.currentHoverState.unitId, state);
        // }
    }

    private renderHexHighlight(hexId: string, state: any = useBattleStore.getState()): void {
        const hex = this.registry.getHex(hexId);
        if (!hex) return;

        const isHovered = this.currentHoverState.hexId === hexId;

        let style = null;

        if (state.selectedSkillId) {
            if (state.targetHexes.includes(hexId)) {
                const unitId = state.battleMap.get(hexId)?.entityId
                const unitOnHex = unitId ? state.units.get(unitId) : null;
                const isEnemy = unitOnHex?.faction === FactionType.Enemy;
                
                if (isEnemy) {
                    style = isHovered ? HEX_STYLES.ATTACK_HOVER : HEX_STYLES.ATTACK_BASE;
                } else {
                    style = isHovered ? HEX_STYLES.HOVER_DEFAULT : HEX_STYLES.ATTACK_EMPTY;
                }
            }
        } else if (state.zocHexes?.has(hexId)) {
            style = isHovered ? HEX_STYLES.ZOC_HOVER : HEX_STYLES.ZOC;
        } else if (state.reachableHexesWithCost?.has(hexId)) {
            const storeHex = state.battleMap.get(hexId);
            const unitOnHex = storeHex?.entityId === state.selectedUnitId;
            if (unitOnHex) {
                style = HEX_STYLES.ACTIVE_UNIT;
            } else {
                style = isHovered ? HEX_STYLES.REACHABLE_HOVER : HEX_STYLES.REACHABLE;
            }
        } else if (state.inspectedUnitId && state.battleMap.get(hexId)?.entityId === state.inspectedUnitId) {
            style = HEX_STYLES.INSPECTED_UNIT;
        } else if (isHovered) {
            style = HEX_STYLES.HOVER_DEFAULT;
        }
// console.log('renderHexHighlight', style)
        // ВИКЛИК НОВОГО МЕТОДУ
        hex.applyHighlight(style);
    }

    /**
     * Єдиний метод для рендеру гекса. Розраховує як базовий, так і hover стан.
     */

    // ═══════════════════════════════════════════════════════════════════════
    // HOVER MANAGEMENT
    // ═══════════════════════════════════════════════════════════════════════

    updateHoverState(hexId: string | null, unitId: string | null, force: boolean = false): void {
        if (!this.stateMachine.allowsHover() && !force) {
            if (this.currentHoverState.hexId || this.currentHoverState.unitId) {
                this.clearHoverState();
            }
            return;
        }

        if (!force && (this.currentHoverState.hexId === hexId && this.currentHoverState.unitId === unitId)) {
            return;
        }

        const state = useBattleStore.getState();
        const prevHexId = this.currentHoverState.hexId;

        this.currentHoverState = { hexId, unitId };

        // Знімаємо старий hover
        if (prevHexId && prevHexId !== hexId) {
            this.renderHexHighlight(prevHexId, state);
        }

        // Застосовуємо новий hover
        if (hexId) this.renderHexHighlight(hexId, state);
    }

    clearHoverState(): void {
        const state = useBattleStore.getState();
        const prevHexId = this.currentHoverState.hexId;

        this.currentHoverState = { hexId: null, unitId: null };

        if (prevHexId) this.renderHexHighlight(prevHexId, state);
    }

    async flashHex(hexId: string): Promise<void> {
        const hex = this.registry.getHex(hexId);
        if (!hex) return;
        // await hex.flashHighlight(HEX_HIGHLIGHT_COLOR, 0.2, 0.15);
        this.renderHexHighlight(hexId);
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PUBLIC API
    // ═══════════════════════════════════════════════════════════════════════

    updateSkillRangeHighlights(): void {
        this.refreshAllHighlights();
    }

    updateReachableHighlights(): void {
        this.refreshAllHighlights();
    }

    resetAllHexes(): void {
        const hexes = this.registry.getAllHexes();
        hexes.forEach(h => h.applyHighlight(null));
    }

    resetAllUnits(): void {
        const units = this.registry.getAllUnits();
        units.forEach(u => {u.stopIdle(); u.stopBadgeHighlight()});
    }

    // ═══════════════════════════════════════════════════════════════════════
    // HIT TESTING
    // ═══════════════════════════════════════════════════════════════════════

    findHexAtPosition(x: number, y: number): BattleHex | null {
        
        const hexCoords = pixelToAxial(x, y);
        const hexId = coordsToId(hexCoords);
        
        return this.registry.getHex(hexId) || null;
        // for (const [_id, hex] of hexes) {
        //     if (hex.containsPoint(x, y)) return hex;
        // }
        // return null;
    }
}